'use client';

import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Gift, MessageCircle, Send, X } from 'lucide-react';

import {
  clearPersistedReferralCode,
  getPersistedReferralCode,
  useAuth,
  useBodyScrollLock,
  useDebounce,
  useT,
} from '@/hooks';
import { useReferralMutation, useValidateReferralCode } from '@/services/query/referral.queries';

import { MESSAGES } from '@/constants/messages.constants';
import { PLATFORM_SOCIAL_LINKS } from '@/constants/platform.constants';

interface InviteGateModalProps {
  isOpen?: boolean;
  onClose?: () => void;
}

export const InviteGateModal: React.FC<InviteGateModalProps> = ({
  isOpen: propIsOpen,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useT();
  const { user, isAuthenticated, isLoading } = useAuth();
  const [code, setCode] = useState(() => {
    const persisted = getPersistedReferralCode();
    return persisted ? persisted.toUpperCase() : '';
  });

  const debouncedCode = useDebounce(code, 500);
  const {
    mutate: validate,
    data: validationResult,
    isPending: isValidating,
  } = useValidateReferralCode();
  const { mutate: applyCode, isPending: isApplying } = useReferralMutation();

  const xLink = PLATFORM_SOCIAL_LINKS.find((l) => l.name === 'X')?.href || '#';
  const telegramLink = PLATFORM_SOCIAL_LINKS.find((l) => l.name === 'Telegram')?.href || '#';

  const isPending = isValidating;

  // Locally dismissed via the close button. Needed because `isOpen` is also driven
  // by the auto-gate condition below, so calling `onClose` alone wouldn't hide it.
  const [dismissed, setDismissed] = useState(false);

  // Re-opening the modal (e.g. from the referral button flips `propIsOpen` true)
  // clears a prior dismissal — the React-recommended "adjust state on prop change"
  // pattern, done during render rather than in an effect.
  const [prevPropIsOpen, setPrevPropIsOpen] = useState(propIsOpen);
  if (propIsOpen !== prevPropIsOpen) {
    setPrevPropIsOpen(propIsOpen);
    if (propIsOpen && dismissed) setDismissed(false);
  }

  // A code captured from an invite link (TikTok/Instagram, /r/CODE, ?ref=…) is
  // persisted by ReferralTracker. If the user is authenticated but not yet
  // referred and such a code is pending, apply it automatically instead of
  // popping the manual-entry box. This covers in-app browsers where the user
  // already has a session, so the normal apply-on-login path never runs.
  const [autoApplyStatus, setAutoApplyStatus] = useState<'idle' | 'applying' | 'failed'>('idle');
  const autoAppliedRef = useRef(false);

  const persistedCode =
    isAuthenticated && !isLoading && !user?.isReferredUser ? getPersistedReferralCode() : null;

  useEffect(() => {
    if (autoAppliedRef.current || !persistedCode) return;
    autoAppliedRef.current = true;
    setAutoApplyStatus('applying');
    applyCode(persistedCode.trim(), {
      onSuccess: () => {
        clearPersistedReferralCode();
        onClose?.();
      },
      onError: () => {
        // Invalid/expired link code — drop it and fall back to manual entry,
        // pre-filling what we had so the user can correct it.
        clearPersistedReferralCode();
        setCode(persistedCode.toUpperCase());
        setAutoApplyStatus('failed');
      },
    });
  }, [persistedCode, applyCode, onClose]);

  const isOpen =
    !dismissed &&
    (propIsOpen ||
      (isAuthenticated &&
        !isLoading &&
        !user?.isReferredUser &&
        autoApplyStatus !== 'applying' &&
        (autoApplyStatus === 'failed' || !persistedCode)));

  useEffect(() => {
    if (debouncedCode && debouncedCode.length >= 4) {
      validate(debouncedCode);
    }
  }, [debouncedCode, validate]);

  useBodyScrollLock(!!isOpen);

  const handleClose = () => {
    setDismissed(true);
    onClose?.();
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = code.trim();

    if (!trimmed) {
      toast.error(MESSAGES.REFERRAL.CODE_REQUIRED);
      return;
    }

    applyCode(trimmed, {
      onSuccess: () => {
        toast.success(MESSAGES.REFERRAL.APPLY_SUCCESS);
        if (onClose) onClose();
      },
      onError: (err: any) => {
        toast.error(err?.message || MESSAGES.REFERRAL.APPLY_ERROR);
      },
    });
  };

  return (
    <Dialog
      open={!!isOpen}
      onClose={() => {}}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '32px',
          bgcolor: 'background.paper',
          backgroundImage:
            theme.palette.mode === 'dark'
              ? `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.95)} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
              : 'none',
          border: '1px solid',
          borderColor: 'divider',
          p: 1,
          position: 'relative',
          overflow: 'visible',
          backdropFilter: 'blur(10px)',
          boxShadow:
            theme.palette.mode === 'dark'
              ? `0 24px 48px ${alpha('#000', 0.5)}`
              : `0 24px 48px ${alpha(theme.palette.primary.main, 0.1)}`,
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        aria-label={t('common.close', { defaultValue: 'Close' })}
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 1,
          width: 32,
          height: 32,
          color: 'text.secondary',
          bgcolor: (theme) =>
            theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
          border: (theme) =>
            theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.1)'
              : '1px solid rgba(0,0,0,0.08)',
          '&:hover': {
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.08)',
            color: 'text.primary',
          },
        }}
      >
        <X size={16} />
      </IconButton>

      <DialogContent>
        <Box sx={{ textAlign: 'center', mb: 4, mt: 2 }}>
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: '20px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
              boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.15)}`,
            }}
          >
            <Gift size={32} />
          </Box>

          <Typography variant="h5" fontWeight={800} gutterBottom>
            {t('referral.haveCode')}
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, px: 2 }}>
            {t('referral.enterCodeDesc')}
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              placeholder={t('referral.enterCodePlaceholder')}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              disabled={isApplying}
              error={validationResult?.isValid === false}
              helperText={
                validationResult?.isValid
                  ? t('referral.referredBy', { name: validationResult.referrerName })
                  : validationResult?.isValid === false
                    ? t('referral.invalidCode')
                    : ''
              }
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  bgcolor: 'action.hover',
                  fontSize: '1rem',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                  '& fieldset': { border: 'none' },
                  '&.Mui-focused fieldset': {
                    border: '1px solid',
                    borderColor: validationResult?.isValid ? 'success.main' : 'primary.main',
                  },
                },
                '& .MuiFormHelperText-root': {
                  color: validationResult?.isValid ? 'success.main' : 'error.main',
                  fontWeight: 600,
                  mx: 1,
                },
                mb: 2,
              }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              type="submit"
              disabled={isPending || !code.trim()}
              startIcon={<Send size={18} />}
              sx={{
                borderRadius: '14px',
                py: 1.8,
                fontWeight: 800,
                textTransform: 'none',
                boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.25)}`,
                '&:hover': {
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.35)}`,
                },
              }}
            >
              {isPending ? t('referral.applying') : t('referral.applyCode')}
            </Button>
          </form>

          {(telegramLink.length > 0 || xLink.length > 0) && (
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={500}
                sx={{ opacity: 0.8 }}
              >
                {t('referral.noCodePrompt')}
              </Typography>

              <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                {telegramLink.length > 0 && (
                  <Box
                    component="a"
                    href={telegramLink}
                    target="_blank"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        opacity: 0.8,
                      },
                    }}
                  >
                    <MessageCircle size={18} />
                    {t('referral.telegram')}
                  </Box>
                )}
                {xLink.length > 0 && (
                  <Box
                    component="a"
                    href={xLink}
                    target="_blank"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        opacity: 0.8,
                      },
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 900, fontSize: '1rem' }}>
                      𝕏
                    </Typography>
                    {t('referral.twitter')}
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};
